import SerializeSimplified from "@/core/misc/decorators/SerializeSimplified";
import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";

import TYPES from "@/core/services/Changelogs/types";

import Core from "@/core/Core";
import Mismatch from "@/core/services/Mismatch";
import AbstractOrderEntity from '@/domains/Abstract/AbstractOrderEntity';
import DateSimple from '@/core/types/DateSimple';
import IntRange from "@/core/types/IntRange";
import ProductCategory from "@/domains/ProductCategories/ProductCategory";
import Product from "@/domains/Products/Product";
import Unit from "@/domains/Units/Unit";
import Manufacturer from "@/domains/Manufacturers/Manufacturer";
import Offer from "@/domains/Offers/Offer";
import Qty from "@/core/types/Qty";
import Price from "@/core/types/Price";
import Sum from "@/core/types/Sum";
import VatRate from "@/core/types/VatRate";

import type Company from "@/domains/Companies/Company";
import DateRange from "@/core/types/DateRange";

import type Demand from "@/domains/Demand/Demand";
import type PreorderPosition from "@/domains/Preorders/PreorderPosition";
import type Order from "@/domains/Orders/Order";
import type TimeStamp from "@/core/types/TimeStamp";
import NoSerialize from "@/core/misc/decorators/NoSerialize";
import type DemandOption from "@/domains/DemandsV2/DemandOption";
import TenderBid from "@/domains/TenderBids/TenderBid";
import Tender from "@/domains/Tenders/Tender";

/**
 * Абстрактная позиция заказа — общие поля товара, цен, количества
 */
abstract class AbstractOrderPosition extends AbstractOrderEntity {

  // ---- Ссылки ----
  @SerializeSimplified
  public offer: Offer | { id: number; version?: number; description?: string } | null;
  @SerializeSimplified
  public demand: Demand | { id: number } | null;

  // ---- Товар ----
  @SerializeSimplified
  protected _productCategory: ProductCategory | null;
  @SerializeSimplified
  public product: Product;

  @Title_ru_RU('ед. измерения', TYPES.OBJECT)
  @SerializeSimplified
  public unit: Unit;
  @Title_ru_RU('фасовка', TYPES.NUMBER)
  public packageVolume: Qty | null;
  @Title_ru_RU('наименование', TYPES.STRING)
  public name: string | null;
  @Title_ru_RU('производитель', TYPES.OBJECT)
  @SerializeSimplified
  public manufacturer: Manufacturer | null;

  @Title_ru_RU('ссылка (номер макета)', TYPES.STRING)
  public layoutFile: string | null;
  @Title_ru_RU('материал', TYPES.STRING)
  public material: string | null;
  @Title_ru_RU('метка утверждённости', TYPES.BOOLEAN)
  public isApproved: boolean | null;

  @Title_ru_RU('артикул поставщика', TYPES.STRING)
  public sellersCode: string | null;
  @Title_ru_RU('артикул покупателя', TYPES.STRING)
  public buyersCode: string | null;

  @Title_ru_RU('количество', TYPES.NUMBER)
  protected _qty: Qty;

  // ---- Цены продавца ----
  @Title_ru_RU('цена продавца', TYPES.NUMBER)
  protected _priceSeller: Price;
  @Title_ru_RU('ставка НДС продавца', TYPES.NUMBER)
  protected _vatRateSeller: VatRate;
  @Title_ru_RU('полная цена продавца', TYPES.NUMBER)
  public priceFullSeller: Price;
  @Title_ru_RU('сумма продавца', TYPES.SUM)
  public sumSeller: Sum;
  @Title_ru_RU('полная сумма продавца', TYPES.SUM)
  public sumFullSeller: Sum;
  @Title_ru_RU('сумма НДС продавца', TYPES.SUM)
  public vatSumSeller: Sum | null;

  // ---- Цены покупателя ----
  @Title_ru_RU('цена покупателя', TYPES.NUMBER)
  public priceBuyer: Price | null;
  public vatRateBuyer: VatRate | null;
  public isVatIncludedBuyer: boolean | null;
  @Title_ru_RU('полная цена покупателя', TYPES.NUMBER)
  public priceFullBuyer: Price | null;
  @Title_ru_RU('сумма покупателя', TYPES.SUM)
  public sumBuyer: Sum | null;
  @Title_ru_RU('полная сумма покупателя', TYPES.SUM)
  public sumFullBuyer: Sum | null;
  @Title_ru_RU('сумма НДС покупателя', TYPES.SUM)
  public vatSumBuyer: Sum | null;

  public productExpirationDate: DateSimple | null;

  @Title_ru_RU('температура хранения', TYPES.INT_RANGE, IntRange.diffIntRange)
  public storeTemperatureRange: IntRange = new IntRange();
  @Title_ru_RU('особые условия перевозки', TYPES.STRING)
  public storeRulesText: string | null;

  // ---- Доставка (специфично для позиции) ----
  @Title_ru_RU('стоимость доставки за ед. товара (при доставке платформой)', TYPES.STRING)
  public deliveryPricePerUnit: Price | null;

  // FIXME что это и зачем?
  @Title_ru_RU('сумма доставки за ед. товара (при доставке платформой)', TYPES.STRING)
  public platformDeliverySum: Price | null;

  /** Документ, которому принадлежит позиция. Связь ставит Order.addPosition. */
  @NoSerialize
  public order: Order | { id: number, notes: string, createdAt: TimeStamp | null } | null;

  /** Является черновиком */
  public isDraft: boolean;

  /**
   * Позиция, из которой эта скопирована (добавление из истории закупок).
   * Живёт только на фронте: связывает копию со строкой реестра-источника,
   * чтобы копии не приходилось нести чужой id.
   */
  @NoSerialize
  public sourcePositionId: number | null;

  /**
   * Привязка к документу: общие реквизиты по ссылке, обратная ссылка,
   * дата создания (нужна для запроса курса) и ставка НДС юрлица продавца.
   */
  setOrder(order: Order): void {
    super.setOrder(order);

    this.order = order;
    this.requisites = order.requisites;
    this.createdAt = order.createdAt;

    const vatRateSeller = order.sellerCompany.vatRate;

    if (order.isFreeSamples) {
      this._priceSeller = Price.zero()
      this.priceFullSeller = Price.zero()
      this.priceBuyer = Price.zero()
      this.priceFullBuyer = Price.zero()
    }

    /** null — ставку не из чего взять: у неплательщика она нулевая, а не null. */
    if (vatRateSeller !== null) {
      this._vatRateSeller = vatRateSeller;
    }
  }

  static fromServer(data: any, result: AbstractOrderPosition): AbstractOrderPosition {
    super.fromServer(data, result);

    //в позицию приходит урезанный объект офера, это является предусмотренным контрактом
    //public offer: Offer | { id: number; version?: number; description?: string } | null;
    //если data.offer !== null а Offer.fromServer выкидывает ошибку, значит объект офера урезанный

    try {
      result.offer = data.offer === null
        ? null
        : Core.utils.hydrate(data.offer, Offer.fromServer);
    } catch (error) {
      result.offer = data.offer as {
        id: number;
        version?: number;
        description?: string;
      };
    }

    // ---- Товар ----
    /** Категорию кладём напрямую в _protected, минуя сеттер: сеттер вывел бы
     *  ставку покупателя из категории, а с бэка она приходит своя и является
     *  источником истины. */
    result._productCategory = Core.utils.hydrate(data.productCategory, ProductCategory.fromServer);
    result.product = Core.utils.hydrate(data.product, Product.fromServer)!;

    result.unit = Core.utils.hydrate(data.unit, Unit.fromServer)!;
    result.packageVolume = Core.utils.hydrate(data.packageVolume, Qty.fromServer)!;
    result.manufacturer = Core.utils.hydrate(data.manufacturer, Manufacturer.fromServer);

    result._qty = Core.utils.hydrate(data.qty, Qty.fromServer)!;

    // ---- Цены (суммы — с бэка, источник истины) ----
    result._priceSeller = Core.utils.hydrate(data.priceSeller, Price.fromServer)!;
    result.priceFullSeller = Core.utils.hydrate(data.priceFullSeller, Price.fromServer)!;
    result._vatRateSeller = Core.utils.hydrate(data.vatRateSeller, VatRate.fromServer)!;
    result.sumSeller = Core.utils.hydrate(data.sumSeller, Sum.fromServer)!;
    result.sumFullSeller = Core.utils.hydrate(data.sumFullSeller, Sum.fromServer)!;
    result.vatSumSeller = Core.utils.hydrate(data.vatSumSeller, Sum.fromServer);

    result.priceBuyer = Core.utils.hydrate(data.priceBuyer, Price.fromServer);
    result.priceFullBuyer = Core.utils.hydrate(data.priceFullBuyer, Price.fromServer);
    result.sumBuyer = Core.utils.hydrate(data.sumBuyer, Sum.fromServer);
    result.sumFullBuyer = Core.utils.hydrate(data.sumFullBuyer, Sum.fromServer);
    result.vatSumBuyer = Core.utils.hydrate(data.vatSumBuyer, Sum.fromServer);
    result.vatRateBuyer = Core.utils.hydrate(data.vatRateBuyer, VatRate.fromServer);

    // ---- Температурный режим ----
    result.storeTemperatureRange = Core.utils.hydrate(data.storeTemperatureRange, IntRange.fromServer)!;
    result.productExpirationDate = Core.utils.hydrate(data.productExpirationDate, DateSimple.fromServer);

    result.deliveryPricePerUnit = Core.utils.hydrate(data.deliveryPricePerUnit, Price.fromServer);
    result.platformDeliverySum = Core.utils.hydrate(data.platformDeliverySum, Price.fromServer)!;

    return result;
  }

  /**
   * Позиция из опции потребности V2.
   * Товар берётся с потребности — у опции его нет; наименование, фасовка,
   * производитель и цены — с опции.
   */
  static fromDemandV2Option(usedOption: {
    option: DemandOption,
    usedQty: Qty
  }, result: AbstractOrderPosition): AbstractOrderPosition {
    super.fromDemandV2Option(usedOption, result);

    const option = usedOption.option;
    const demandV2 = option.demand;

    // ---- Товар ----
    /** Ставку покупателя выводит сеттер категории — отдельного присваивания
     *  здесь нет. */
    result.productCategory = demandV2.productCategory;
    result.product = demandV2.product;

    result.unit = option.unit!;
    result.packageVolume = option.packageVolume;
    result.name = option.name;
    result.manufacturer = option.manufacturer;
    result.sellersCode = option.sellersCode;
    result.buyersCode = demandV2.product?.code;

    result._qty = usedOption.usedQty;

    // ---- Цены продавца ----
    result._priceSeller = option.priceSeller;
    result._vatRateSeller = option.vatRateSeller!;

    // ---- Хранение ----
    result.storeTemperatureRange = option.storeTemperatureRange;
    result.productExpirationDate = option.productExpirationDate;

    // ---- Доставка ----
    result.deliveryPricePerUnit = option.deliveryPricePerUnit;

    result.recalcSellerPricesAndSums();

    return result;
  }

  /**
   * Позиция из предложения.
   * Товар оффер не знает — его даёт потребность, если заказ создаётся под неё.
   */
  static fromOffer(offer: Offer, demand: Demand | null, result: AbstractOrderPosition): AbstractOrderPosition {
    super.fromOffer(offer, result);

    // ---- Ссылки ----
    result.offer = offer;
    result.demand = demand;

    // ---- Товар ----
    /** Ставку покупателя выводит сеттер категории — отдельного присваивания
     *  здесь нет. */
    result.productCategory = offer.productCategory;

    if (demand === null) {
      // товара нет: у оффера только категория
    } else {
      result.product = demand.product;
      result.buyersCode = demand.product?.code;
    }

    result.unit = offer.unit!;
    result.packageVolume = offer.packageVolume;
    result.name = offer.name;
    result.manufacturer = offer.manufacturer;
    result.sellersCode = offer.sellersCode;

    // ---- Цены продавца ----
    result._priceSeller = offer.priceSeller;
    result._vatRateSeller = offer.vatRateSeller!;

    // ---- Цены покупателя ----
    /** Условия НДС покупателя берём из ставки — там же, где посчитана его цена. */
    result.isVatIncludedBuyer = offer.isVatIncludedBuyer;
    result.vatRateBuyer = offer.vatRateBuyer;

    if (Core.currentPartner.isSecondaryBuyer === true) {
      /** Второй покупатель третий знак не принимает — цена идёт в копейках. */
      result.priceFullBuyer = offer.priceFullBuyer!.roundTo2Decimals();
    }
    else {
      result.priceFullBuyer = offer.priceFullBuyer;
    }

    // ---- Хранение ----
    result.storeTemperatureRange = offer.storeTemperatureRange;
    result.productExpirationDate = offer.productExpirationDate;

    // ---- Доставка ----
    result.deliveryPricePerUnit = offer.deliveryPricePerUnit;

    result.recalcSellerPricesAndSums();
    result.recalcBuyerPricesAndSums();

    return result;
  }

  static fromTenderBid(tenderBid: TenderBid, tender: Tender, result: AbstractOrderPosition) {
    super.fromTenderBid(tenderBid, tender, result);

    // ---- Ссылки ----
    result.offer = tenderBid.offer;
    result.demand = tender.demand;
    result.product = tender.product;
    result.buyersCode = tender.product?.code;


    // ---- Товар ----
    /** Ставку покупателя выводит сеттер категории — отдельного присваивания
     *  здесь нет. */
    result.productCategory = tender.productCategory;

    if (tenderBid.qtyLeft?.isEmpty() === true) {
      result._qty = Qty.zero();
    } else {
      if (tenderBid.qtyLeft!.greaterThan(result.demand.qtyDesired) === true) {
        result._qty = result.demand.qtyDesired
      } else {
        result._qty = tenderBid.qtyLeft!;
      }
    }

    result.unit = tenderBid.unit!;
    result.packageVolume = tenderBid.packageVolume;
    result.name = tenderBid.name;
    result.manufacturer = tenderBid.manufacturer;
    result.sellersCode = tenderBid.sellersCode;

    // ---- Цены продавца ----
    result._priceSeller = tenderBid.priceSeller;
    result._vatRateSeller = tenderBid.vatRateSeller!;

    // ---- Цены покупателя ----
    /** Условия НДС покупателя берём из ставки — там же, где посчитана его цена. */
    result.isVatIncludedBuyer = tenderBid.isVatIncludedBuyer;
    result.vatRateBuyer = tenderBid.vatRateBuyer;

    if (Core.currentPartner.isSecondaryBuyer === true) {
      /** Второй покупатель третий знак не принимает — цена идёт в копейках. */
      result.priceFullBuyer = tenderBid.priceFullBuyer!.roundTo2Decimals();
    }
    else {
      result.priceFullBuyer = tenderBid.priceFullBuyer;
    }

    // ---- Хранение ----
    result.storeTemperatureRange = tenderBid.storeTemperatureRange;
    result.productExpirationDate = tenderBid.productExpirationDate;

    // ---- Доставка ----
    result.deliveryPricePerUnit = tenderBid.deliveryPricePerUnit;

    result.recalcSellerPricesAndSums();
    result.recalcBuyerPricesAndSums();

    return result;
  }

  /**
   * Позиция из позиции предзаказа.
   * Количество не переносится: сколько выбрать из остатка, решает пользователь.
   */
  static fromPreorderPosition(preorderPosition: PreorderPosition, result: AbstractOrderPosition): AbstractOrderPosition {
    super.fromPreorderPosition(preorderPosition, result);

    // ---- Ссылки ----
    result.offer = preorderPosition.offer;

    // ---- Товар ----
    result.productCategory = preorderPosition.productCategory;
    result.product = preorderPosition.product;

    result.unit = preorderPosition.unit;
    result.packageVolume = preorderPosition.packageVolume;
    result.name = preorderPosition.name;
    result.manufacturer = preorderPosition.manufacturer;
    result.sellersCode = preorderPosition.sellersCode;
    result.buyersCode = preorderPosition.buyersCode;
    result.material = preorderPosition.material;
    result.layoutFile = preorderPosition.layoutFile;

    // ---- Цены продавца ----
    result._priceSeller = preorderPosition.priceSeller;
    result._vatRateSeller = preorderPosition.vatRateSeller!;

    // ---- Цены покупателя ----
    /** Ставка предзаказа зафиксирована и может отличаться от категорийной,
     *  поэтому перебивает выведенную сеттером категории. */
    result.vatRateBuyer = preorderPosition.vatRateBuyer;
    result.isVatIncludedBuyer = preorderPosition.isVatIncludedBuyer;

    // ---- Хранение ----
    result.storeTemperatureRange = preorderPosition.storeTemperatureRange;
    result.productExpirationDate = preorderPosition.productExpirationDate;
    result.storeRulesText = preorderPosition.storeRulesText;

    // ---- Доставка ----
    result.deliveryPricePerUnit = preorderPosition.deliveryPricePerUnit;

    return result;
  }

  /**
   * Дата курса валюты — сегодня. Позицию смотрят, чтобы понять, за сколько
   * можно купить сейчас, а не сколько она стоила в момент заказа.
   *
   * Симметрично AbstractOrderOption.getDateForCurrencyRate.
   */
  getDateForCurrencyRate(): DateSimple {
    return DateSimple.today();
  }

  /**
   * Причины, по которым нельзя менять собственные поля позиции — цену,
   * валюту, ставку НДС, наименование, условия перевозки.
   * Пустой массив — менять можно.
   *
   * База причин не знает: состояние процесса живёт в наследниках, у которых
   * есть статусы отгрузки и оплаты.
   */
  getReasonsCannotChangePosition(): string[] {
    return [];
  }

  /**
   * Причины, по которым нельзя сменить товар позиции.
   * Пустой массив — менять можно.
   */
  getReasonsCannotChangeProduct(): string[] {
    return [];
  }

  /**
   * Причины, по которым нельзя менять реквизиты сделки на позиции —
   * юр. лицо продавца, производственную площадку, тип доставки.
   * Пустой массив — менять можно.
   *
   * Блок определяется по позиции:
   * позиция предзаказа - редактируем,
   * позиция заказа на основе предзаказа - редактируем,
   * обычная позиция заказа - нет, идем в карточку заказа, чтобы это поменять в согласии с другими позициями в заказе
   */
  getReasonsCannotChangeDealTerms(): string[] {
    return this.requisites.getReasonsCannotChangeDealTerms();
  }

  /** Релевантный код по роли зрителя — для колонки "Арт" в реестрах */
  get code(): string | null {
    if (Core.currentPartner.isSeller) {
      return this.sellersCode;
    }
    return this.buyersCode;
  }

  /**
   * Период получения. Значение производное — бэк его не отдаёт и не хранит,
   * поэтому поля здесь нет: каждый наследник выводит период из своих дат.
   */
  abstract get receiptPeriod(): DateRange | null;

  /** ================ Сравнение с требованием ================ */

  /**
   * Прокси к правилам Mismatch. Позиция всегда сравнивается с потребностью,
   * поэтому разрешений на поставку здесь нет: требование к производителю
   * приходит списком допустимых.
   */

  getReceiptDateMismatch(target: DateSimple): string | null {
    if (Core.utils.isNullOrUndefined(this.receiptPeriod) === true) {
      return null;
    } else {
      return Mismatch.getReceiptDateMismatch(this.receiptPeriod!, target);
    }
  }

  getQtyMismatch(target: Qty): string | null {
    return Mismatch.getQtyMismatch(this.qty, target, this.unit);
  }

  getExpirationDateMismatch(target: DateSimple): string | null {
    if (Core.utils.isNullOrUndefined(this.productExpirationDate)) {
      return null;
    } else {
      return Mismatch.getExpirationDateMismatch(this.productExpirationDate!, target);
    }
  }

  getManufacturerMismatch(target: Manufacturer[]): string | null {
    if (Core.utils.isNullOrUndefined(this.manufacturer)) {
      return null;
    } else if (Core.utils.isEmptyArray(target)) {
      return null;
    } else {
      return Mismatch.getManufacturerMismatchByList(this.manufacturer!, target);
    }
  }

  getSellerCompanyMismatch(target: Company[]): string | null {
    if (Core.utils.isEmptyArray(target)) {
      return null;
    } else {
      return Mismatch.getSellerCompanyMismatchByList(this.sellerCompany, target);
    }
  }

  getUnitMismatch(target: Unit): string | null {
    return Mismatch.getUnitMismatch(this.unit, target);
  }

  // ====================

  /**
   * Категория товара. Ставка НДС покупателя от неё производна, поэтому
   * выводится здесь, а не в каждой фабрике: путь создания позиции может
   * быть любым (оффер, потребность, предзаказ, ручной ввод в диалоге),
   * а источник ставки один.
   */
  get productCategory(): ProductCategory | null {
    return this._productCategory;
  }

  set productCategory(value: ProductCategory | null) {
    this._productCategory = value;

    /** Ставка покупателя — свойство товара, а не поставщика: берём из
     *  категории. Категория без ставки — откат на локальную юрисдикцию,
     *  как в DemandV2.fromNew. */
    if (Core.utils.isNullOrUndefined(this._productCategory?.vatRate)) {
      this.vatRateBuyer = VatRate.fromServer(Core.config.localDefaultVatRate);
    } else {
      this.vatRateBuyer = this._productCategory!.vatRate;
    }
  }

  // ==================== Количество, цена, ставка НДС ====================
  // isVatIncludedSeller НЕ здесь: он живёт на общих requisites (один на заказ
  // и все позиции). Его смена должна пересчитывать суммы всех позиций сразу,
  // а не одной — это гейт canChangeCriticalFields, а не сеттер позиции.

  get qty(): Qty {
    return this._qty;
  }

  set qty(value: Qty) {
    this._qty = value;
    this.recalcSellerPricesAndSums();
    this.recalcBuyerPricesAndSums();
  }

  get priceSeller(): Price {
    return this._priceSeller;
  }

  set priceSeller(value: Price) {
    this._priceSeller = value;
    this.recalcSellerPricesAndSums();
  }

  get vatRateSeller(): VatRate | null {
    return this._vatRateSeller;
  }

  set vatRateSeller(value: VatRate | null) {
    this._vatRateSeller = value;
    this.recalcSellerPricesAndSums();
  }

  protected recalcSellerPricesAndSums(): void {
    // Пользователь только создал позицию, заполнил количество, но ещё не добрался до контрола цены
    if (this._priceSeller === undefined || this._priceSeller.isEmpty()) return;

    this.priceFullSeller = this._priceSeller.getFull(this.isVatIncludedSeller, this.vatRateSeller);

    this.sumSeller = this._priceSeller.mul(this._qty);
    this.sumFullSeller = this.sumSeller.getFull(this.isVatIncludedSeller, this.vatRateSeller);
    this.vatSumSeller = this.sumSeller.getVat(this.isVatIncludedSeller, this.vatRateSeller);
  }

  /** Зеркало recalcSellerPricesAndSums для стороны покупателя. */
  // работает только при наличии priceFullBuyer, получаем его при создании заказа из ставки
  protected recalcBuyerPricesAndSums(): void {
    if (Core.utils.isNullOrUndefined(this.priceBuyer) === false && this.priceBuyer!.isEmpty() === false) {
      // чистая цена уже есть — выводить нечего
    }
    else if (Core.utils.isNullOrUndefined(this.priceFullBuyer) === false && this.priceFullBuyer!.isEmpty() === false) {
      /** Ставка тендера несёт только полную цену покупателя — чистую выводим
       *  из неё, дальше расчёт идёт как у продавца. */
      this.priceBuyer = this.priceFullBuyer!.getNet(this.isVatIncludedBuyer, this.vatRateBuyer);
    }
    else {
      // Пользователь только создал позицию, заполнил количество, но ещё не добрался до контрола цены
      return;
    }

    // this.priceFullBuyer = this.priceBuyer!.getFull(this.isVatIncludedBuyer, this.vatRateBuyer);

    this.sumBuyer = this.priceBuyer!.mul(this._qty);
    this.sumFullBuyer = this.sumBuyer.getFull(this.isVatIncludedBuyer, this.vatRateBuyer);
    this.vatSumBuyer = this.sumBuyer.getVat(this.isVatIncludedBuyer, this.vatRateBuyer);
  }
}

export default AbstractOrderPosition;
